import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import _ from "lodash";
import { Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import ContentWrapper from "../Layout/ContentWrapper";
import { TabSwitch } from "../customComponents";
import {
  getResultsFullAction,
  getResultsKOAction,
  getKnockoutGamesAction,
} from "../../store/reducers/results";
import { getCountryInfo } from "../../utils/countryUtils";
import { useContestId } from "../../hooks/useContestId";

const GROUP_ORDER = [
  "A", "B", "C", "D", "E", "F",
  "G", "H", "I", "J", "K", "L",
];

// Knockout rounds in bracket order, with the round_name values each maps to.
// Real data uses the short enum ('R32'/'R16'/'QF'/'SF'/'F'); the ?ko_demo data
// uses the spelled-out names — accept both.
const KO_ROUND_DEFS = [
  { keys: ["R32", "Round of 32"], label: "Round of 32" },
  { keys: ["R16", "Round of 16"], label: "Round of 16" },
  { keys: ["QF", "Quarterfinals", "Quarter-finals"], label: "Quarterfinals" },
  { keys: ["SF", "Semifinals", "Semi-finals"], label: "Semifinals" },
  { keys: ["F", "Final", "Finals"], label: "Final" },
];
const KO_THIRD_KEYS = ["3rd", "Third Place", "3rd Place", "Third-place"];

// Format a UTC match datetime for the bracket cards in the viewer's local time,
// e.g. "Jun 28, 3:00 PM". Returns "" for missing/unparseable values.
const formatKODate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// TEMP — in-memory sample knockout bracket for ?ko_demo=1. Lets us exercise the
// full KnockoutList render (round labels, winner muting, and the "Your pick"
// correctness coloring: cyan = right, red = wrong, gray = pending) WITHOUT
// seeding the live, contest-scoped knockout_results table. Shape mirrors the
// results_ko payload exactly. Delete this with the koDemo gate in <Results/>
// once real R32 data is flowing.
const KO_DEMO_MATCHES = [
  {
    match_number: 73, round_name: "Round of 32",
    team_1_number: 1, team_1_name: "Argentina", team_1_world_rank: 1, team_1_flag: null,
    team_2_number: 26, team_2_name: "Australia", team_2_world_rank: 26, team_2_flag: null,
    winner_team_number: 1,
    user_pick_name: "Argentina", user_pick_winner: 1, // correct -> cyan
  },
  {
    match_number: 74, round_name: "Round of 32",
    team_1_number: 2, team_1_name: "France", team_1_world_rank: 2, team_1_flag: null,
    team_2_number: 40, team_2_name: "Nigeria", team_2_world_rank: 40, team_2_flag: null,
    winner_team_number: 40,
    user_pick_name: "France", user_pick_winner: 2, // wrong -> red
  },
  {
    match_number: 75, round_name: "Round of 32",
    team_1_number: 3, team_1_name: "Brazil", team_1_world_rank: 3, team_1_flag: null,
    team_2_number: 23, team_2_name: "Korea Republic", team_2_world_rank: 23, team_2_flag: null,
    winner_team_number: null,
    user_pick_name: "Brazil", user_pick_winner: 3, // pending -> gray
  },
  {
    match_number: 76, round_name: "Round of 32",
    team_1_number: 4, team_1_name: "England", team_1_world_rank: 4, team_1_flag: null,
    team_2_number: 20, team_2_name: "Senegal", team_2_world_rank: 20, team_2_flag: null,
    winner_team_number: 4,
    user_pick_name: "England", user_pick_winner: 4, // correct -> cyan
  },
  {
    match_number: 89, round_name: "Round of 16",
    team_1_number: 1, team_1_name: "Argentina", team_1_world_rank: 1, team_1_flag: null,
    team_2_number: 40, team_2_name: "Nigeria", team_2_world_rank: 40, team_2_flag: null,
    winner_team_number: null,
    user_pick_name: "Argentina", user_pick_winner: 1, // pending -> gray
  },
];

const Results = (props) => {
  const { tab } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const contestId = useContestId();
  // TEMP — ?ko_demo=1 renders KO_DEMO_MATCHES (in-memory) instead of live data
  // so the KO results view can be tested end-to-end without writing to the live
  // contest tables. Gated on the query param, so no real user ever sees it.
  const koDemo = searchParams.get("ko_demo") === "1";

  const userId = useSelector((state) => state.app?.userId);
  const score = useSelector((state) => state.app?.score);

  const groupStandings = useSelector((state) => state.results?.groupStandings);
  const knockoutResults = useSelector(
    (state) => state.results?.knockoutResults,
  );
  const knockoutGames = useSelector((state) => state.results?.knockoutGames);
  const contestType = useSelector((state) => state.app?.contestType);
  const isKnockout = contestType === "Knockout";
  const loading = useSelector((state) => state.results?.loading);
  const currentStage = useSelector((state) => state.app?.currentStage);
  // The user's tiebreaker guess (total goals over the whole tournament) comes
  // from /status via app state; shown alongside the actual group-stage total.
  const tiebreakerGuess = useSelector(
    (state) => state.app?.picksProgress?.tiebreakerAnswer,
  );

  // The final 32 are locked in when the group stage goes final, at which point
  // current_stage advances past "group". Until then we hide the per-group
  // "Correct Picks" tally (nothing is decided yet) and label the table column
  // "Current Standing"; once announced it becomes "Final Standing".
  const final32Announced =
    !!currentStage && !["not_started", "group"].includes(currentStage);

  const [currTab, setCurrTab] = useState(tab || "group");

  // The Results header (title + subtitle + tab toggle) sticks beneath the frozen
  // Nav while scrolling the day's games. Nav height is dynamic (mobile vs
  // desktop, and it grows once the games strip loads), so measure it live.
  const headerRef = useRef(null);
  const [navOffset, setNavOffset] = useState(0);
  useEffect(() => {
    const nav = document.getElementById("app-nav");
    if (!nav) return;
    const update = () => setNavOffset(nav.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  // Measure this sticky Results header so the knockout round-label strip can
  // pin just beneath it (header height is dynamic: mobile vs desktop layout).
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const update = () => setHeaderH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Track element for the sticky round labels; translated horizontally in
  // lockstep with the bracket's own horizontal scroll (see KnockoutBracket).
  const headerTrackRef = useRef(null);

  useEffect(() => {
    setCurrTab(tab || "group");
  }, [tab]);

  // A Knockout-type contest has no group stage — force the knockout view and
  // hide the group tab regardless of the incoming tab prop / entry routing.
  useEffect(() => {
    if (isKnockout) setCurrTab("knockout");
  }, [isKnockout]);

  // results_full carries group standings + third place + knockout_results
  useEffect(() => {
    if (!userId) return;
    dispatch(getResultsFullAction({ userId, contestId }));
  }, [dispatch, userId, contestId]);

  // knockout tab additionally pulls the per-team progression view
  useEffect(() => {
    if (!userId || currTab !== "knockout") return;
    dispatch(getResultsKOAction({ userId, contestId }));
  }, [dispatch, userId, contestId, currTab]);

  // knockout tab also pulls the full bracket skeleton (all 32 matches incl.
  // unresolved TBD slots) so the bracket shows its complete shape from the
  // start; user picks + results are overlaid from knockout_results above.
  useEffect(() => {
    if (currTab !== "knockout") return;
    dispatch(getKnockoutGamesAction({ contestId }));
  }, [dispatch, contestId, currTab]);

  const handleTabChange = (event, newTab) => {
    if (newTab) setCurrTab(newTab);
  };

  const orderedGroups = useMemo(() => {
    const keys = Object.keys(groupStandings || {});
    return _.sortBy(keys, (k) => {
      const idx = GROUP_ORDER.indexOf(k);
      return idx === -1 ? 99 : idx;
    });
  }, [groupStandings]);

  // ---------- small presentational helpers ----------
  // Total goals scored across the whole group stage = sum of every team's GF
  // (each goal is one team's "goals scored", so this is the tournament total).
  const totalGroupGoals = useMemo(() => {
    let sum = 0;
    Object.values(groupStandings || {}).forEach((rows) => {
      (rows || []).forEach((r) => {
        if (_.isNumber(r.goals_scored)) sum += r.goals_scored;
      });
    });
    return sum;
  }, [groupStandings]);

  // Total goals across knockout matches that have been played (both team scores
  // per match; -1 = not yet played, so skipped). For a Knockout contest this is
  // the tournament total the tiebreaker guess is shown against.
  //
  // Read from knockoutGames — the knockout_stage_games skeleton that carries
  // the live per-slot scores filled by the SDIO sync — NOT knockout_results,
  // which has no goal columns and isn't populated by the live results sync.
  // Coerce with Number(): the API can return goals as strings, and _.isNumber
  // would then reject them, which is why this read 0 despite scores being live.
  const totalKnockoutGoals = useMemo(() => {
    let sum = 0;
    (knockoutGames || []).forEach((m) => {
      const g1 = Number(m?.team_1_goals);
      const g2 = Number(m?.team_2_goals);
      if (Number.isFinite(g1) && g1 >= 0) sum += g1;
      if (Number.isFinite(g2) && g2 >= 0) sum += g2;
    });
    return sum;
  }, [knockoutGames]);

  const countryCode = (name) => getCountryInfo(name)?.code || "";

  const TeamCell = ({ flag, name, worldRank, muted, subline }) => (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      sx={{ alignItems: "center", gap: "10px", minWidth: 0 }}
    >
      <Grid
        container
        sx={{
          width: "32px",
          minWidth: "32px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {flag ? (
          <img
            src={flag}
            alt={`${name || "team"}-flag`}
            style={{ maxWidth: "32px", maxHeight: "20px", borderRadius: "2px" }}
          />
        ) : (
          <Grid
            sx={{
              width: "32px",
              height: "20px",
              borderRadius: "2px",
              backgroundColor: theme.branding.gray[200],
            }}
          />
        )}
      </Grid>
      <Grid container direction="column" sx={{ minWidth: 0 }}>
        <Grid
          container
          direction="row"
          wrap="wrap"
          sx={{ alignItems: "center", columnGap: "12px", rowGap: "2px", minWidth: 0 }}
        >
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{ alignItems: "center", gap: "6px", width: "auto", minWidth: 0 }}
          >
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                color: muted ? theme.branding.gray[400] : "inherit",
                fontWeight: 500,
              }}
            >
              {name || "TBD"}
            </Typography>
            {!!worldRank && worldRank > 0 && (
              <Typography
                variant="caption"
                sx={{ color: theme.branding.gray[400], fontWeight: 500 }}
              >
                #{worldRank}
              </Typography>
            )}
          </Grid>
          {/* stats sit inline, on the same line as the team name + rank */}
          {subline}
        </Grid>
        {!!name && (
          <Typography
            variant="caption"
            sx={{ color: theme.branding.gray[400] }}
          >
            {countryCode(name)}
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  // Small pill for a pick result: a glyph (✓ / ✗) or a text label.
  const ResultChip = ({ glyph, label, color, bg }) => (
    <Grid
      container
      sx={{
        padding: "4px 10px",
        borderRadius: "8px",
        backgroundColor: bg,
        width: "fit-content",
        minWidth: glyph ? "28px" : 0,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontSize: "13px", lineHeight: 1, fontWeight: 700, color }}
      >
        {glyph || label}
      </Typography>
    </Grid>
  );

  // The user makes exactly 32 advancement predictions (top 2 of each group +
  // their 8 best thirds). Only those picks can score, so only they get a result
  // chip — a pick the user did NOT predict to reach the Round of 32 (4th place,
  // and thirds outside their top 8) shows nothing. pickPredictedAdvance mirrors
  // the pick's user_knockout_simple.is_in_knockout ("Yes" for exactly the 32);
  // pickAdvanced mirrors the picked team's actual is_round_of_32.
  const PickResult = ({ pickPredictedAdvance, pickAdvanced }) => {
    const green = "#16A34A";
    const red = theme.branding.red[500];

    if (pickPredictedAdvance !== "Yes") return null;

    if (pickAdvanced === "Yes") {
      return <ResultChip glyph="✓" color={green} bg="#16A34A1A" />;
    }
    if (pickAdvanced === "No") {
      return <ResultChip glyph="✗" color={red} bg="#FB2C361A" />;
    }
    return (
      <ResultChip
        label="Pending"
        color={theme.branding.gray[400]}
        bg={theme.branding.gray[100]}
      />
    );
  };

  // W/L/D record + goals for/against for the current group standing
  const StandingStats = ({ wins, losses, ties, goalsScored, goalsAllowed }) => {
    const w = _.isNumber(wins) ? wins : 0;
    const l = _.isNumber(losses) ? losses : 0;
    const d = _.isNumber(ties) ? ties : 0;
    const gf = _.isNumber(goalsScored) ? goalsScored : 0;
    const ga = _.isNumber(goalsAllowed) ? goalsAllowed : 0;

    return (
      <Grid
        container
        direction="row"
        wrap="wrap"
        sx={{ gap: "2px 10px", alignItems: "center", width: "auto" }}
      >
        <Typography
          variant="caption"
          sx={{ color: theme.branding.gray[500], fontWeight: 600 }}
        >
          {w}W · {l}L · {d}D
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: theme.branding.gray[400] }}
        >
          GF {gf} · GA {ga}
        </Typography>
      </Grid>
    );
  };

  const cardSx = [
    {
      width: "100%",
      borderRadius: "14px",
      border: `1px solid ${theme.branding.gray[200]}`,
      backgroundColor: theme.branding.gray[0],
      overflow: "hidden",
    },
    (theme) =>
      theme.applyStyles("dark", {
        borderColor: theme.branding.darkBlue[600],
        backgroundColor: theme.branding.darkBlue[800],
      }),
  ];

  const EmptyState = ({ text }) => (
    <Grid
      container
      sx={[
        {
          borderRadius: "14px",
          width: "100%",
          padding: isMobile ? "48px 16px" : "96px 16px",
          backgroundColor: theme.branding.gray[0],
          border: `1px solid ${theme.branding.gray[200]}`,
          justifyContent: "center",
          alignItems: "center",
          gap: "12px",
        },
        (theme) =>
          theme.applyStyles("dark", {
            borderColor: theme.branding.darkBlue[600],
            backgroundColor: theme.branding.darkBlue[800],
          }),
      ]}
    >
      <EmojiEventsOutlinedIcon
        sx={{ color: theme.branding.gray[400], height: "20px", width: "20px" }}
      />
      <Typography variant="subtitle1" sx={{ color: theme.branding.gray[400] }}>
        {text}
      </Typography>
    </Grid>
  );

  // ---------- group standings card ----------
  const GroupStandingsCard = ({ groupName, rows }) => {
    const ordered = _.sortBy(rows, (r) => r.finish_position);
    // Correct advancement picks = the green checks: every team the user placed
    // in a 1st/2nd slot or a top-8 third (pick_predicted_advance = 'Yes') whose
    // placed team actually reached the Round of 32.
    const correctCount = ordered.filter(
      (r) => r.pick_predicted_advance === "Yes" && r.pick_advanced === "Yes",
    ).length;

    return (
      <Grid container direction="column" sx={cardSx}>
        {/* card header */}
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{
            padding: "14px 20px",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.branding.gray[200]}`,
          }}
        >
          <Typography variant="h4" sx={{ fontSize: "16px" }}>
            Group {groupName}
          </Typography>
          {/* Correct-picks tally is meaningless until the group stage is final
              (the 32 advancers are known), so hide it until then. */}
          {final32Announced && (
            <Grid
              container
              sx={{
                width: "auto",
                padding: "2px 10px",
                borderRadius: "8px",
                backgroundColor: "#33DDEE1A",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: theme.branding.cyan[600], fontWeight: 600 }}
              >
                Correct Picks: {correctCount}
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* column labels */}
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{
            padding: "8px 20px",
            gap: "12px",
            backgroundColor: theme.branding.gray[100],
          }}
        >
          <Typography
            variant="caption"
            sx={{ width: "28px", color: theme.branding.gray[500] }}
          >
            #
          </Typography>
          <Typography
            variant="caption"
            sx={{ flex: 1, color: theme.branding.gray[500] }}
          >
            {final32Announced ? "Final Standing" : "Current Standing"}
          </Typography>
          {/* result column now sits between the standing and the user's pick */}
          <Typography
            variant="caption"
            sx={{
              width: "90px",
              minWidth: "90px",
              color: theme.branding.gray[500],
            }}
          >
            Result
          </Typography>
          <Typography
            variant="caption"
            sx={{ flex: 1, color: theme.branding.gray[500] }}
          >
            Your Picks
          </Typography>
        </Grid>

        {/* rows */}
        {ordered.map((row) => (
          <Grid
            key={`${groupName}-${row.finish_position}-${row.group_standing_number}`}
            container
            direction="row"
            wrap="nowrap"
            sx={{
              padding: "12px 20px",
              gap: "12px",
              alignItems: "center",
              borderTop: `1px solid ${theme.branding.gray[200]}`,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ width: "28px", color: theme.branding.gray[500] }}
            >
              {row.finish_position}
            </Typography>
            <Grid sx={{ flex: 1, minWidth: 0 }}>
              <TeamCell
                flag={row.team_flag}
                name={row.country_name}
                worldRank={row.team_world_rank}
                subline={
                  <StandingStats
                    wins={row.wins}
                    losses={row.losses}
                    ties={row.ties}
                    goalsScored={row.goals_scored}
                    goalsAllowed={row.goals_allowed}
                  />
                }
              />
            </Grid>
            {/* result column — between Current Standing and Your Picks */}
            <Grid
              container
              sx={{
                width: "90px",
                minWidth: "90px",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              {row.pick_team_number > 0 && (
                <PickResult
                  pickPredictedAdvance={row.pick_predicted_advance}
                  pickAdvanced={row.pick_advanced}
                />
              )}
            </Grid>
            {/* the user's pick for this slot */}
            <Grid sx={{ flex: 1, minWidth: 0 }}>
              {row.pick_team_number && row.pick_team_number > 0 ? (
                <TeamCell
                  flag={row.pick_flag}
                  name={row.pick_country_name}
                  worldRank={row.pick_world_rank}
                  muted
                />
              ) : (
                <Typography
                  variant="subtitle1"
                  sx={{ color: theme.branding.gray[400] }}
                >
                  No pick
                </Typography>
              )}
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  };

  // ---------- total goals + tiebreaker card ----------
  // Big actual total with the user's tiebreaker guess beneath. Parameterized so
  // both the group view (group-stage goals) and the knockout view (tournament
  // goals) can show it — a Knockout contest has no group tab, so this is how its
  // Results page still surfaces the tiebreaker guess.
  const TotalGoalsCard = ({ title, total, caption }) => (
    <Grid container direction="column" sx={cardSx}>
      <Grid
        container
        sx={{
          padding: "14px 20px",
          borderBottom: `1px solid ${theme.branding.gray[200]}`,
        }}
      >
        <Typography variant="h4" sx={{ fontSize: "16px" }}>
          {title}
        </Typography>
      </Grid>
      <Grid
        container
        direction="column"
        sx={{
          padding: "24px 20px",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
        }}
      >
        <Typography
          sx={{
            fontSize: "44px",
            fontWeight: 700,
            lineHeight: 1,
            color: theme.branding.cyan[600],
          }}
        >
          {total}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ color: theme.branding.gray[500], textAlign: "center" }}
        >
          {caption}
        </Typography>
        {_.isNumber(tiebreakerGuess) && tiebreakerGuess !== -1 && (
          <Typography
            variant="subtitle1"
            sx={{ color: theme.branding.gray[400], textAlign: "center" }}
          >
            Your tiebreaker guess: {tiebreakerGuess}
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  // ---------- knockout bracket ----------
  // Full left-to-right bracket built from the knockout_games skeleton (all 32
  // matches incl. unresolved TBD slots); results + the user's per-match pick are
  // overlaid from knockout_results. One column per round (R32 → Final); each
  // match is centered between the two matches feeding it (winner_to_match_number,
  // index-pairing fallback) and joined to them by elbow connectors. Winner row
  // is bold + ✓; a left accent shows whether YOUR pick won (cyan) or lost (red).
  // The 3rd-place playoff sits in its own card. Scrolls horizontally.
  const KnockoutBracket = () => {
    // skeleton = full bracket once knockout_games has loaded; fall back to the
    // resolved-only knockout_results (and the in-memory ?ko_demo set) otherwise.
    const skeleton = koDemo
      ? KO_DEMO_MATCHES
      : knockoutGames && knockoutGames.length
        ? knockoutGames
        : knockoutResults || [];

    // user's pick per match (team_number picked to win) keyed by match_number,
    // from knockout_results — present for every recorded match (the demo set
    // carries it inline, handled in MatchCard).
    const userPickByMatch = {};
    (koDemo ? KO_DEMO_MATCHES : knockoutResults || []).forEach((m) => {
      if (m && m.match_number != null && m.user_pick_winner != null)
        userPickByMatch[Number(m.match_number)] = Number(m.user_pick_winner);
    });

    const rawRounds = KO_ROUND_DEFS.map((def) => ({
      def,
      matches: _.sortBy(
        (skeleton || []).filter((m) => def.keys.includes(m.round_name)),
        (m) => Number(m.match_number),
      ),
    })).filter((r) => r.matches.length > 0);

    // Order each column top-to-bottom by walking the bracket TREE
    // (winner_to_match_number) from the final back to the round of 32, so the
    // two matches feeding a given match are always ADJACENT. That is what makes
    // the elbow connectors nest cleanly instead of crossing — match_number
    // order does not match the bracket wiring (e.g. 74 & 77 both feed 89, but
    // sit far apart numerically). Falls back to the numeric order when the
    // wiring is incomplete (demo data / partial bracket), preserving behavior.
    const orderRoundsByBracket = (rs) => {
      if (rs.length < 2) return rs;
      const ordered = rs.map(() => []);
      let wiringComplete = true;
      const kidsFromRound = (parentMn, round) =>
        round.matches
          .filter((c) => Number(c.winner_to_match_number) === parentMn)
          .sort((a, b) => Number(a.match_number) - Number(b.match_number));
      const walk = (match, ri) => {
        ordered[ri].push(match);
        if (ri === 0) return;
        const kids = kidsFromRound(Number(match.match_number), rs[ri - 1]);
        if (kids.length !== 2) {
          wiringComplete = false;
          return;
        }
        kids.forEach((k) => walk(k, ri - 1));
      };
      [...rs[rs.length - 1].matches]
        .sort((a, b) => Number(a.match_number) - Number(b.match_number))
        .forEach((root) => walk(root, rs.length - 1));
      const placed = ordered.reduce((n, arr) => n + arr.length, 0);
      const total = rs.reduce((n, r) => n + r.matches.length, 0);
      if (!wiringComplete || placed !== total) return rs;
      return rs.map((r, ri) => ({ ...r, matches: ordered[ri] }));
    };

    const rounds = orderRoundsByBracket(rawRounds);

    const third = (skeleton || []).filter((m) =>
      KO_THIRD_KEYS.includes(m.round_name),
    );

    if (_.isEmpty(rounds) && _.isEmpty(third)) {
      return (
        <EmptyState text="Knockout results will appear once the round of 32 begins." />
      );
    }

    // layout constants
    const COL_W = isMobile ? 176 : 220;
    const COL_GAP = isMobile ? 30 : 48;
    const CARD_H = 104; // match #/time header + location + two team rows
    const ROW_H = 22;
    const SLOT = isMobile ? 124 : 132; // vertical pitch between adjacent leaf (R32) matches
    const HEADER_H = 28;

    // y (top px, within the funnel) per match_number: leaves evenly spaced;
    // each later match centered on the matches feeding it.
    const yByMatch = {};
    rounds.forEach((round, ri) => {
      round.matches.forEach((m, mi) => {
        const mn = Number(m.match_number);
        if (ri === 0) {
          yByMatch[mn] = mi * SLOT;
          return;
        }
        const prev = rounds[ri - 1].matches;
        let kids = prev.filter((c) => Number(c.winner_to_match_number) === mn);
        if (kids.length === 0)
          kids = [prev[mi * 2], prev[mi * 2 + 1]].filter(Boolean);
        const ys = kids
          .map((c) => yByMatch[Number(c.match_number)])
          .filter((v) => v !== undefined);
        yByMatch[mn] = ys.length
          ? ys.reduce((a, b) => a + b, 0) / ys.length
          : mi * SLOT;
      });
    });

    const colX = (ri) => ri * (COL_W + COL_GAP);
    const maxY = Object.values(yByMatch).reduce((a, b) => Math.max(a, b), 0);
    const funnelH = maxY + CARD_H + 8;
    // Trailing gutter so the rightmost (Final) card isn't flush against the
    // horizontal-scroll edge, which clips its border / rounded corner and reads
    // as the Final tile being slightly cut off. Keyed off totalW so the canvas,
    // SVG connector layer, and sticky round-label strip widen together.
    const EDGE_PAD = 24;
    const totalW =
      rounds.length * COL_W +
      Math.max(0, rounds.length - 1) * COL_GAP +
      EDGE_PAD;

    // Place the third-place playoff in the Semifinal column (the open gap to the
    // LEFT of the Final), vertically level with the Final card — matching the
    // Picks page — rather than below the Final.
    const finalMatch = rounds[rounds.length - 1]?.matches?.[0];
    const finalMn = finalMatch ? Number(finalMatch.match_number) : null;
    const THIRD_LABEL_H = 18;
    const thirdLeftCol = Math.max(0, rounds.length - 2);
    const thirdTop = finalMn != null ? yByMatch[finalMn] : 0;
    const thirdLabelTop = Math.max(0, thirdTop - THIRD_LABEL_H - 4);
    const thirdBottom =
      !_.isEmpty(third) && finalMn != null ? thirdTop + CARD_H : 0;
    const totalH = Math.max(funnelH, thirdBottom) + 8;

    // elbow connectors: each child's right-middle → mid-gap → parent left-middle
    const connectors = [];
    rounds.forEach((round, ri) => {
      if (ri === 0) return;
      const prev = rounds[ri - 1].matches;
      const parentX = colX(ri);
      const childRightX = colX(ri - 1) + COL_W;
      const midX = childRightX + COL_GAP / 2;
      round.matches.forEach((m, mi) => {
        const mn = Number(m.match_number);
        const parentMidY = yByMatch[mn] + CARD_H / 2;
        let kids = prev.filter((c) => Number(c.winner_to_match_number) === mn);
        if (kids.length === 0)
          kids = [prev[mi * 2], prev[mi * 2 + 1]].filter(Boolean);
        kids.forEach((c) => {
          const cy = yByMatch[Number(c.match_number)] + CARD_H / 2;
          connectors.push(
            `M ${childRightX} ${cy} H ${midX} V ${parentMidY} H ${parentX}`,
          );
        });
      });
    });
    const connectorColor = "rgba(148,163,184,0.55)";

    const MatchCard = ({ m, left, top }) => {
      const decided = Number(m.winner_team_number) > 0;
      const mn = Number(m.match_number);
      const userPick =
        m.user_pick_winner != null
          ? Number(m.user_pick_winner)
          : userPickByMatch[mn] != null
            ? userPickByMatch[mn]
            : null;
      const pickWon =
        decided && userPick != null && userPick === Number(m.winner_team_number);
      const pickLost =
        decided &&
        userPick != null &&
        userPick > 0 &&
        userPick !== Number(m.winner_team_number);
      const accent = pickWon
        ? theme.branding.cyan[500]
        : pickLost
          ? theme.branding.red[500]
          : "transparent";
      const positioned = top != null && left != null;

      const TeamRow = ({ num, name, flag, worldRank, goals }) => {
        const isWinner =
          decided && Number(m.winner_team_number) === Number(num);
        const isLoser = decided && !isWinner && Number(num) > 0;
        const hasTeam = Number(num) > 0;
        return (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{ alignItems: "center", gap: "6px", minWidth: 0, height: `${ROW_H}px` }}
          >
            {flag ? (
              <img
                src={flag}
                alt=""
                style={{ width: "22px", height: "14px", borderRadius: "2px", flexShrink: 0 }}
              />
            ) : (
              <Grid
                sx={{
                  width: "22px", height: "14px", borderRadius: "2px",
                  backgroundColor: theme.branding.gray[200], flexShrink: 0,
                }}
              />
            )}
            <Typography
              noWrap
              variant="subtitle2"
              sx={{
                flex: 1, minWidth: 0,
                fontWeight: isWinner ? 700 : 500,
                color: isLoser ? theme.branding.gray[400] : "inherit",
              }}
            >
              {hasTeam ? name || "TBD" : "TBD"}
            </Typography>
            {hasTeam && Number(worldRank) > 0 && (
              <Typography
                variant="caption"
                sx={{ color: theme.branding.gray[400], fontWeight: 500, flexShrink: 0 }}
              >
                #{worldRank}
              </Typography>
            )}
            {decided && (
              <Typography
                variant="subtitle2"
                sx={{
                  width: "16px", textAlign: "right", flexShrink: 0,
                  fontWeight: isWinner ? 700 : 500,
                  color: isLoser ? theme.branding.gray[400] : "inherit",
                }}
              >
                {Number(goals) >= 0 ? goals : ""}
              </Typography>
            )}
            {isWinner && (
              <Typography
                variant="caption"
                sx={{ color: theme.branding.cyan[600], fontWeight: 700, flexShrink: 0 }}
              >
                ✓
              </Typography>
            )}
          </Grid>
        );
      };

      const when = formatKODate(m.match_datetime_utc);

      return (
        <Grid
          container
          direction="column"
          sx={[
            ...cardSx,
            {
              height: `${CARD_H}px`,
              padding: "6px 10px",
              gap: "2px",
              justifyContent: "flex-start",
              borderLeft: `3px solid ${accent}`,
              boxSizing: "border-box",
              ...(positioned
                ? {
                    position: "absolute",
                    top: `${top}px`,
                    left: `${left}px`,
                    width: `${COL_W}px`,
                    zIndex: 1,
                  }
                : { width: "100%", maxWidth: `${COL_W}px` }),
            },
          ]}
        >
          {/* card header: kickoff date/time */}
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{ alignItems: "center", justifyContent: "flex-end", gap: "6px", minWidth: 0 }}
          >
            {!!when && (
              <Typography
                noWrap
                variant="caption"
                sx={{ color: theme.branding.gray[400], minWidth: 0 }}
              >
                {when}
              </Typography>
            )}
          </Grid>

          {/* venue */}
          {!!m.location && (
            <Typography
              noWrap
              variant="caption"
              sx={{ color: theme.branding.gray[400], minWidth: 0 }}
            >
              {m.location}
            </Typography>
          )}

          {decided ? (
            // Match is final — collapse to just the winning team rather than
            // showing both R32 teams with the loser greyed out.
            <TeamRow
              num={m.winner_team_number}
              name={
                Number(m.team_1_number) === Number(m.winner_team_number)
                  ? m.team_1_name
                  : m.team_2_name
              }
              flag={
                Number(m.team_1_number) === Number(m.winner_team_number)
                  ? m.team_1_flag
                  : m.team_2_flag
              }
              worldRank={
                Number(m.team_1_number) === Number(m.winner_team_number)
                  ? m.team_1_world_rank
                  : m.team_2_world_rank
              }
              goals={
                Number(m.team_1_number) === Number(m.winner_team_number)
                  ? m.team_1_goals
                  : m.team_2_goals
              }
            />
          ) : (
            <>
              <TeamRow
                num={m.team_1_number}
                name={m.team_1_name}
                flag={m.team_1_flag}
                worldRank={m.team_1_world_rank}
                goals={m.team_1_goals}
              />
              <TeamRow
                num={m.team_2_number}
                name={m.team_2_name}
                flag={m.team_2_flag}
                worldRank={m.team_2_world_rank}
                goals={m.team_2_goals}
              />
            </>
          )}
        </Grid>
      );
    };

    return (
      <Grid container direction="column" sx={{ gap: "16px", width: "100%" }}>
        {koDemo && (
          <Grid
            container
            sx={{
              width: "100%", padding: "8px 12px", borderRadius: "8px",
              backgroundColor: "#FFF3CD", justifyContent: "center",
            }}
          >
            <Typography variant="caption" sx={{ color: "#8A6D00", fontWeight: 600 }}>
              Demo data — sample bracket, not live results
            </Typography>
          </Grid>
        )}

        {!_.isEmpty(rounds) && (
          <>
            {/* sticky round-header strip — pinned just beneath the Results
                header and translated horizontally to match the bracket's own
                horizontal scroll below */}
            <Grid
              sx={[
                {
                  position: "sticky",
                  top: `${navOffset + headerH}px`,
                  zIndex: 50,
                  width: "100%",
                  overflow: "hidden",
                  backgroundColor: theme.branding.gray[0],
                  paddingBottom: "4px",
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Grid
                ref={headerTrackRef}
                sx={{
                  position: "relative",
                  width: `${totalW}px`,
                  height: `${HEADER_H}px`,
                }}
              >
                {rounds.map((round, ri) => (
                  <Typography
                    key={`hdr-${round.def.label}`}
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: `${colX(ri)}px`,
                      width: `${COL_W}px`,
                      textAlign: "center",
                      color: theme.branding.cyan[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {round.def.label}
                  </Typography>
                ))}
              </Grid>
            </Grid>

            <Grid
              onScroll={(e) => {
                if (headerTrackRef.current) {
                  headerTrackRef.current.style.transform = `translateX(${-e.currentTarget.scrollLeft}px)`;
                }
              }}
              sx={{ width: "100%", overflowX: "auto", paddingBottom: "8px" }}
            >
              <Grid
                sx={{
                  position: "relative",
                  width: `${totalW}px`,
                  height: `${totalH}px`,
                  minWidth: "100%",
                }}
              >
              {/* connector layer (behind the cards) */}
              <svg
                width={totalW}
                height={totalH}
                style={{
                  position: "absolute", top: 0, left: 0,
                  pointerEvents: "none", overflow: "visible",
                }}
              >
                {connectors.map((d, i) => (
                  <path
                    key={i}
                    d={d}
                    fill="none"
                    stroke={connectorColor}
                    strokeWidth="1.5"
                  />
                ))}
              </svg>

              {/* match cards */}
              {rounds.map((round, ri) =>
                round.matches.map((m) => (
                  <MatchCard
                    key={`ko-${m.match_number}`}
                    m={m}
                    left={colX(ri)}
                    top={yByMatch[Number(m.match_number)]}
                  />
                )),
              )}

              {/* third-place playoff — Semifinal column, level with the Final */}
              {!_.isEmpty(third) && finalMn != null && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      left: `${colX(thirdLeftCol)}px`,
                      top: `${thirdLabelTop}px`,
                      width: `${COL_W}px`,
                      color: theme.branding.gray[500],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Third-place playoff
                  </Typography>
                  <MatchCard
                    m={third[0]}
                    left={colX(thirdLeftCol)}
                    top={thirdTop}
                  />
                </>
              )}
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  return (
    <ContentWrapper>
      <TabContext value={currTab}>
        <Grid
          container
          direction="column"
          sx={{ width: "100%", gap: "24px" }}
        >
          {/* header — sticky beneath the frozen Nav so the title and the
              Group Stage / Knockout toggle stay reachable while scrolling */}
          <Grid
            container
            ref={headerRef}
            direction={isMobile ? "column" : "row"}
            wrap="nowrap"
            sx={[
              {
                position: "sticky",
                top: `${navOffset}px`,
                zIndex: 60,
                backgroundColor: theme.branding.gray[0],
                gap: "16px",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                padding: "16px 0",
                borderBottom: `1px solid ${theme.branding.gray[200]}`,
              },
              (theme) =>
                theme.applyStyles("dark", {
                  backgroundColor: theme.branding.darkBlue[800],
                }),
            ]}
          >
            <Grid container direction="column" sx={{ gap: "8px" }}>
              <Typography variant="h2">Results</Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: theme.branding.gray[500] }}
              >
                World Cup 2026 · how your picks are scoring
              </Typography>
              {/* Picks tally (counts) — moved here from the Nav. Fed by
                  /status -> overall_score, the same opportunity set the score
                  uses, so it stays in step with the Score readout. */}
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={{
                  width: "auto",
                  gap: "16px",
                  alignItems: "baseline",
                  marginTop: "2px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 700,
                    color: theme.branding.cyan[600],
                  }}
                >
                  Picks
                </Typography>
                {[
                  {
                    label: "Total",
                    value: Number(score?.total_picks) || 0,
                    color: "inherit",
                  },
                  {
                    label: "Correct",
                    value: Number(score?.correct_picks) || 0,
                    color: theme.branding.cyan[600],
                  },
                  {
                    label: "Missed",
                    value: Number(score?.missed_picks) || 0,
                    color: theme.branding.red[500],
                  },
                ].map((st) => (
                  <Grid
                    key={st.label}
                    container
                    direction="row"
                    wrap="nowrap"
                    sx={{ width: "auto", gap: "6px", alignItems: "baseline" }}
                  >
                    <Typography sx={{ fontWeight: 700, color: st.color }}>
                      {st.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.branding.gray[500] }}
                    >
                      {st.label}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {!isKnockout && (
              <TabSwitch
                tabs={[
                  { label: "Group Stage", value: "group" },
                  { label: "Knockout", value: "knockout" },
                ]}
                currTab={currTab}
                handleTabChange={handleTabChange}
                context="results"
              />
            )}
          </Grid>

          {/* group panel — suppressed for a Knockout-type contest */}
          {!isKnockout && (
          <TabPanel value="group" sx={{ padding: 0 }}>
            {!loading && _.isEmpty(groupStandings) ? (
              <EmptyState text="No group results yet — check back once matches go final." />
            ) : (
              <Grid
                container
                direction="column"
                sx={{ gap: "16px", width: "100%" }}
              >
                {orderedGroups.map((g) => (
                  <GroupStandingsCard
                    key={`standings-${g}`}
                    groupName={g}
                    rows={groupStandings[g]}
                  />
                ))}
                <TotalGoalsCard
                  title="Group stage goals"
                  total={totalGroupGoals}
                  caption="Total goals scored in the entire group tournament"
                />
              </Grid>
            )}
          </TabPanel>
          )}

          {/* knockout panel */}
          <TabPanel value="knockout" sx={{ padding: 0 }}>
            <Grid container direction="column" sx={{ gap: "16px", width: "100%" }}>
              <KnockoutBracket />
              <TotalGoalsCard
                title="Tournament goals"
                total={totalKnockoutGoals}
                caption="Total goals scored so far in the knockout rounds"
              />
            </Grid>
          </TabPanel>
        </Grid>
      </TabContext>
    </ContentWrapper>
  );
};

export default Results;
